import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import { API_BASE_URL } from '../../config/api';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import CityDropdown from '../../components/CityDropdown';

const EditJointVentureProperty = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams();
  const navigate = useNavigate();

  // State for form data
  const [formData, setFormData] = useState({
    Property_Type: '',
    City: '',
    Tehsil: '',
    Mouza: '',
    Acre: '',
    Kanal: '',
    Marla: '',
    Contact_Name: '',
    Contact_Number: '',
    Location: '',
    No_of_Kitchens: '',
    No_of_Beds: '',
    No_Of_Bathrooms: '',
    No_of_Floors: '',
    latitude: '',
    longitude: '',
  });

  // State for loading and error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [errors, setErrors] = useState({});

  // Fetch property data
  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/jvProperty/getjvProperty-byId/${id}`);
        const propertyData = response.data.result;
        
        // Set form data
        setFormData({
          Property_Type: propertyData.Property_Type || '',
          City: propertyData.City || '',
          Tehsil: propertyData.Tehsil || '',
          Mouza: propertyData.Mouza || '',
          Acre: propertyData.Acre || '',
          Kanal: propertyData.Kanal || '',
          Marla: propertyData.Marla || '',
          Contact_Name: propertyData.Contact_Name || '',
          Contact_Number: propertyData.Contact_Number || '',
          Location: propertyData.Location || '',
          No_of_Kitchens: propertyData.No_of_Kitchens || '',
          No_of_Beds: propertyData.No_of_Beds || '',
          No_Of_Bathrooms: propertyData.No_Of_Bathrooms || '',
          No_of_Floors: propertyData.No_of_Floors || '',
          latitude: propertyData.latitude || '',
          longitude: propertyData.longitude || '',
        });

        // Set existing images
        if (propertyData.images && propertyData.images.length > 0) {
          setExistingImages(propertyData.images);
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to fetch property data. Please try again.');
        setLoading(false);
      }
    };

    fetchPropertyData();
  }, [id]);

  // Handle file drop
  const onDrop = (acceptedFiles) => {
    setSelectedFiles([...selectedFiles, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxSize: 5242880, // 5MB
  });

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate City
    if (!formData.City) {
      newErrors.City = 'City is required';
    }

    // Validate Contact Number
    if (!formData.Contact_Number) {
      newErrors.Contact_Number = 'Contact Number is required';
    } else if (!/^03\d{9}$/.test(formData.Contact_Number)) {
      newErrors.Contact_Number = 'Contact Number must be 11 digits starting with 03';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create FormData object
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Append new images
      selectedFiles.forEach(file => {
        formDataToSend.append('images', file);
      });

      // Make API call to update property
      await axios.put(`${API_BASE_URL}/api/jvProperty/updateProperty/${id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/listing/jointventure');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update property. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Remove selected file
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Remove existing image
  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  if (loading && !formData.City) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="Edit Joint Venture Property" subtitle="Update property details" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Property updated successfully! Redirecting...
        </Alert>
      )}

      <Paper
        elevation={3}
        sx={{
          p: 3,
          backgroundColor: colors.primary[400],
          borderRadius: 2,
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Property Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Property Type</InputLabel>
                <Select
                  name="Property_Type"
                  value={formData.Property_Type}
                  onChange={handleInputChange}
                  label="Property Type"
                >
                  <MenuItem value="Residential">Residential</MenuItem>
                  <MenuItem value="Commercial">Commercial</MenuItem>
                  <MenuItem value="Agricultural">Agricultural</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* City */}
            <Grid item xs={12} sm={6}>
              <CityDropdown
                value={formData.City}
                onChange={handleInputChange}
                error={!!errors.City}
                helperText={errors.City}
              />
            </Grid>

            {/* Tehsil */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tehsil"
                name="Tehsil"
                value={formData.Tehsil}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Mouza */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mouza"
                name="Mouza"
                value={formData.Mouza}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Area Measurements */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Acre"
                name="Acre"
                value={formData.Acre}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Kanal"
                name="Kanal"
                value={formData.Kanal}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Marla"
                name="Marla"
                value={formData.Marla}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location"
                name="Location"
                value={formData.Location}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Property Details */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Number of Beds"
                name="No_of_Beds"
                value={formData.No_of_Beds}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Number of Bathrooms"
                name="No_Of_Bathrooms"
                value={formData.No_Of_Bathrooms}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Number of Kitchens"
                name="No_of_Kitchens"
                value={formData.No_of_Kitchens}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Number of Floors"
                name="No_of_Floors"
                value={formData.No_of_Floors}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Name"
                name="Contact_Name"
                value={formData.Contact_Name}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Number"
                name="Contact_Number"
                value={formData.Contact_Number}
                onChange={handleInputChange}
                error={!!errors.Contact_Number}
                helperText={errors.Contact_Number}
                placeholder="03XXXXXXXXX"
                inputProps={{
                  maxLength: 11,
                  pattern: '^03\\d{9}$'
                }}
              />
            </Grid>

            {/* Coordinates */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Latitude"
                name="latitude"
                value={formData.latitude}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Longitude"
                name="longitude"
                value={formData.longitude}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Images */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Property Images
              </Typography>

              {/* Existing Images */}
              {existingImages.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Existing Images
                  </Typography>
                  <Grid container spacing={2}>
                    {existingImages.map((image, index) => (
                      <Grid item key={index}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: 150,
                            height: 150,
                            border: `1px solid ${colors.grey[300]}`,
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={`${API_BASE_URL}/uploads/${image}`}
                            alt={`Property ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeExistingImage(index)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* New Images */}
              <Box
                {...getRootProps()}
                sx={{
                  border: `2px dashed ${colors.grey[300]}`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: colors.primary[500],
                  },
                }}
              >
                <input {...getInputProps()} />
                <Typography>
                  Drag and drop images here, or click to select files
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  (Max file size: 5MB, Supported formats: JPG, JPEG, PNG)
                </Typography>
              </Box>

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Selected Files
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedFiles.map((file, index) => (
                      <Grid item key={index}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: 150,
                            height: 150,
                            border: `1px solid ${colors.grey[300]}`,
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Selected ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeFile(index)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/listing/jointventure')}
                  sx={{
                    color: colors.grey[700],
                    borderColor: colors.grey[700],
                    '&:hover': {
                      borderColor: colors.grey[900],
                      backgroundColor: colors.grey[100],
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    backgroundColor: colors.primary[500],
                    '&:hover': {
                      backgroundColor: colors.primary[600],
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Update Property'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default EditJointVentureProperty; 